import CookieUtility from './utilities/cookie-utility';
import Group from './group';
import OptionData from './types/option-data';
import AbstractOption from './abstract-option';
import Logger from './utilities/logger';
import A11yConsentApi from './a11y-consent-api';

// Positions to inject HTML into
const InjectPosition: { [key: string]: string } = {
  HeadStart: 'headStart',
  HeadEnd: 'headEnd',
  BodyStart: 'bodyStart',
  BodyEnd: 'bodyEnd',
  Inline: 'inline'
};

/**
 * Default consent option, meaning it can be enabled or disabled by the user.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export default class Option extends AbstractOption {
  public group: Group;

  // Unique identifier; helps in identifying if the consent option is currently enabled.
  public readonly identifier: string;

  // Script to inject after `<head>`
  public readonly injectScriptHeadStart: string;

  // Script to inject before `</head>`
  public readonly injectScriptHeadEnd: string;

  // Script to inject after `<body>`
  public readonly injectScriptBodyStart: string;

  // Script to inject before `</body>`
  public readonly injectScriptBodyEnd: string;

  // HTML to inject after `<head>`
  public readonly injectHtmlHeadStart: string;

  // HTML to inject before `</head>`
  public readonly injectHtmlHeadEnd: string;

  // HTML to inject after `<body>`
  public readonly injectHtmlBodyStart: string;

  // HTML to inject before `</body>`
  public readonly injectHtmlBodyEnd: string;

  // Whether this option is already decoded
  public isDecoded: boolean = false;

  constructor(api: A11yConsentApi, group: Group, data: OptionData) {
    super(api, group, data);
    Logger.debug('option', `Starting to construct "${this.uid}".`);

    this.identifier = data.identifier;
    this.group = group;
    this.injectScriptHeadStart = data.injectScriptHeadStart;
    this.injectScriptHeadEnd = data.injectScriptHeadEnd;
    this.injectScriptBodyStart = data.injectScriptBodyStart;
    this.injectScriptBodyEnd = data.injectScriptBodyEnd;
    this.injectHtmlHeadStart = data.injectHtmlHeadStart;
    this.injectHtmlHeadEnd = data.injectHtmlHeadEnd;
    this.injectHtmlBodyStart = data.injectHtmlBodyStart;
    this.injectHtmlBodyEnd = data.injectHtmlBodyEnd;

    Logger.debug('option', `Finished constructing "${this.uid}".`);
  }

  /**
   * Checks if the consent option related to the given identifier has been interacted with in any way, meaning it has
   * either been enabled or disabled.
   *
   * @returns {boolean} True when the option has been interacted with according to a cookie, otherwise false
   */
  public isInteracted(): boolean {
    const isInteracted = CookieUtility.get(this.identifier) !== null;
    if (isInteracted) {
      Logger.info('option', `"${this.identifier}" is interacted with.`);
    } else {
      Logger.info('option', `"${this.identifier}" is not interacted with.`);
    }
    return isInteracted;
  }

  /**
   * Checks if the consent option related to the given identifier is enabled according to the currently chosen user
   * settings.
   *
   * @returns {boolean|null} True when the option is enabled according to a cookie, otherwise false
   */
  public isEnabled(): boolean | null {
    const isEnabled = CookieUtility.get(this.identifier);
    if (isEnabled === null) {
      Logger.info('option', `"${this.identifier}" is not interacted with, therefore also not enabled.`);
      return null;
    } else if (isEnabled === '1') {
      Logger.info('option', `"${this.identifier}" is enabled.`);
      return true;
    } else {
      Logger.info('option', `"${this.identifier}" is not enabled.`);
      return false;
    }
  }

  /**
   * Enables or disables the consent option. This will also automatically trigger the decoding of corresponding content.
   * Enabling a consent options leads to a cookie being created with the same identifier with either the value "1" for
   * enabled, or "0" for disabled. These cookies will carry over to any other TYPO3 site as well, as long as the cookie
   * domain fits. Therefore, even translated pages or pages with the same consent settings keep already chosen cookie
   * settings. If at any point a cookie is missing (see also the `isInteracted()` method), the consent view needs to be
   * show again, as some previously unknown consent option was found.
   *
   * @param {boolean} enabled True to enable this option, otherwise false
   */
  public setEnabled(enabled: boolean): void {
    // Enabled state already correct
    if (enabled == this.isEnabled()) {
      return;
    }

    if (enabled) {
      CookieUtility.set(
        this.identifier,
        '1',
        A11yConsentConfiguration.settings.cookieDomain
      );
    } else {
      CookieUtility.set(
        this.identifier,
        '0',
        A11yConsentConfiguration.settings.cookieDomain
      );
    }

    if (enabled) {
      this.decode();
      this.emitter.emit('enabled', this);
      Logger.info('option', `Enabled "${this.identifier}".`);
    } else {
      this.emitter.emit('disabled', this);
      Logger.info('option', `Disabled "${this.identifier}".`);
    }
  }

  /**
   * Decodes content related to this consent option. Will only decode content, if this option is enabled.
   *
   * @returns {Promise<void>[]|null} All promises for decoding an option; may be empty, if decoding was not allowed or
   * possible
   */
  public decode(): Promise<void>[] {
    if (!this.isEnabled()) {
      Logger.info('option', `"${this.identifier}" is not enabled, so nothing to decode.`);
      return [];
    }
    if (this.isDecoded) {
      Logger.info('option', `"${this.identifier}" is already decoded, so nothing to decode again.`);
      return [];
    }

    // Order of decoding is important here. Placeholders that contain encoded content need to be decoded first, followed
    // by standalone encoded content. If the order was swapped, then `decodeStandalone()` might already decode content
    // within a placeholder. The following `decodePlaceholders()` would then find nothing more to decode, leading to
    // inconsistencies.
    const placeholders = document.querySelectorAll<HTMLElement>(
      `[data-a11y-consent-placeholder="${this.identifier}"]`
    );
    const decodePlaceholderPromises: Promise<void>[] = [];
    for (const placeholder of placeholders) {
      Logger.info('option', `Starting to decode placeholder for "${this.identifier}".`, placeholder);
      decodePlaceholderPromises.push(this.decodePlaceholder(placeholder));
      Logger.info('option', `Finished decoding placeholder for "${this.identifier}".`);
    }

    const encodedContainers = document.querySelectorAll<HTMLElement>(
      `[data-a11y-consent-encoded="${this.identifier}"]`
    );
    const decodeStandalonePromises: Promise<void>[] = [];
    for (const container of encodedContainers) {
      Logger.info('option', `Starting to decode standalone container for "${this.identifier}".`, container);
      decodeStandalonePromises.push(this.decodeStandalone(container));
      Logger.info('option', `Finished decoding standalone container for "${this.identifier}".`);
    }

    const decodePromises: Promise<void>[] = [...decodePlaceholderPromises, ...decodeStandalonePromises];
    Promise.all(decodePlaceholderPromises)
      .then(() => {
        Logger.info('option', 'All decode placeholder promises finished.');
        this.emitter.emit('decodedAllPlaceholders', this);
      });
    Promise.all(decodeStandalonePromises)
      .then(() => {
        Logger.info('option', 'All decode standalone promises finished.');
        this.emitter.emit('decodedAllStandalone', this);
      });
    Promise.all(decodePromises)
      .then(() => {
        Logger.info('option', 'All decode promises finished.');
        this.emitter.emit('decodedAll', this);
      });

    this.isDecoded = true;
    return decodePromises;
  }

  /**
   * Decodes content, which uses a wrapping placeholder to display a preview of the element and probably an activate
   * button. Within the placeholder, a container with encoded content is required.
   *
   * @param {HTMLElement} placeholder Wrapping placeholder container
   */
  private decodePlaceholder(placeholder: HTMLElement): Promise<void> {
    if (!this.isEnabled()) {
      return Promise.reject();
    }

    const ariaLabel = placeholder.dataset.a11yConsentPlaceholderAriaLabel ?? null;
    const encodedContainer = placeholder.querySelector<HTMLElement>(
      `[data-a11y-consent-encoded="${this.identifier}"]`
    );
    const previewContainer = placeholder.querySelector<HTMLElement>('[data-a11y-consent-preview]');

    // If no container with encoded content is found, nothing needs to be decoded
    if (!encodedContainer) {
      Logger.notice('option', `No encoded container found for "${this.identifier}", nothing to decode.`);
      return Promise.reject();
    }

    // Remove the encode-attribute, so that subsequent query selectors for [data-a11y-consent-encoded] will not find
    // this encoded container anymore. Although the placeholder will be removed later from the DOM via
    // placeholder.remove(), the DOM may still need some time to update, leaving a small window where encoded containers
    // can be found even after they are already decoded. This would lead to unnecessary double decoding procedures.
    delete encodedContainer.dataset.a11yConsentEncoded;

    this.emitter.emit('decodePlaceholder', placeholder);
    this.emitter.emit('decode', placeholder);

    Logger.notice('option', `Decoding encoded placeholder container for "${this.identifier}".`);
    return this.api.decode(encodedContainer.innerHTML, A11yConsentConfiguration.settings.baseUrl)
      .then(response => {
        return response.text();
      })
      .then(decodedString => {
        // Find reference to the parent container, which will be used to insert decoded content relatively to it
        const parentContainer = placeholder.parentElement as HTMLElement;

        // Remove the preview container
        if (previewContainer) {
          previewContainer.remove();
        }

        // Move the encoded container after the parent
        parentContainer.insertAdjacentElement('beforeend', encodedContainer);

        // Remove the now deprecated placeholder
        placeholder.remove();

        // Inject the decoded string into the new decoded container
        this.injectHtml(encodedContainer, decodedString);

        // Add label to the now decoded container
        if (ariaLabel !== null) {
          parentContainer.tabIndex = -1;
          parentContainer.ariaLabel = ariaLabel;
        }

        // The event "enableClicked" will add additional information to the element; see API initializeEnableActions()
        this.emitter.emit('decodedPlaceholder', this);
        this.emitter.emit('decoded', this);
      })
      .catch(reason => {
        throw new Error(`Decoding container of placeholder for option "${this.identifier}" failed: ${reason}`);
      });
  }

  /**
   * Decodes content of a standalone non-placeholder container.
   *
   * @param {HTMLElement} encodedContainer Container containing currently encoded content
   */
  private decodeStandalone(encodedContainer: HTMLElement): Promise<void> {
    if (!this.isEnabled()) {
      return Promise.reject();
    }

    this.emitter.emit('decodeStandalone', encodedContainer);
    this.emitter.emit('decode', encodedContainer);

    Logger.notice('option', `Decoding encoded standalone container for "${this.identifier}".`);
    return this.api.decode(encodedContainer.innerHTML, A11yConsentConfiguration.settings.baseUrl)
      .then(response => {
        return response.text();
      })
      .then(decodedString => {
        this.injectHtml(encodedContainer, decodedString);
        this.emitter.emit('decodedStandalone', this);
        this.emitter.emit('decoded', this);
      })
      .catch(reason => {
        throw new Error(`Decoding standalone container for option "${this.identifier}" failed: ${reason}`);
      });
  }

  /**
   * Injects a decoded HTML string into the DOM. Makes sure, that e.g. `<script>...</script>` tags are properly
   * evaluated and some `data-a11y-consent-*` data attributes are removed after decoding.
   *
   * @param {HTMLElement} encodedContainer Container holding the encoded string; will be replaced with the decoded
   * string
   * @param {string} decodedString The decoded string from the encoded container
   */
  private injectHtml(encodedContainer: HTMLElement, decodedString: string): void {
    Logger.info('option', `Injecting HTML for "${this.identifier}".`);

    // Inject content to a specific location
    if (
      encodedContainer.dataset.a11yConsentInjectPosition &&
      Object.values<string>(InjectPosition).includes(encodedContainer.dataset.a11yConsentInjectPosition)
    ) {
      // Decoded string is JavaScript
      Logger.info(
        'option',
        `Injecting HTML for "${this.identifier}" to "${encodedContainer.dataset.a11yConsentInjectPosition}".`
      );

      // Move encoded container to its final location
      encodedContainer = this.moveElementToPosition(
        encodedContainer,
        encodedContainer.dataset.a11yConsentInjectPosition
      );

      // Replace the encoded container with the now decoded children. This will get rid of the initial wrapper.
      // Note, that using procedures like a.outerHTML = b.innerHTML or a.replaceWith(...b.children) will not trigger
      // evaluation of any child-script-tags. Things like insertAdjacentHTML can work, however, that makes it
      // difficult to get rid of the temporary div-wrapper. Iterating over a list of children and trying to insert it
      // via e.g. insertAdjacentElement within that loop is also cumbersome, as the iteration-counter during a for-of
      // loop gets confused.
      encodedContainer.outerHTML = decodedString;

      // Now that everything is in its correct position in the DOM, re-evaluate any tags, if necessary
      document.querySelectorAll<HTMLElement>('[data-a11y-consent-injectable-tag]').forEach(element => {
        if (element.tagName === 'SCRIPT') {
          const newScript = document.createElement('script');
          newScript.innerHTML = element.innerHTML;

          // Copy all attributes over to the new element
          for (const attributeName of element.getAttributeNames()) {
            newScript.setAttribute(attributeName, element.getAttribute(attributeName) ?? '');
          }

          // Delete the marker for evaluating this new script, to prevent multiple executions
          delete newScript.dataset.a11yConsentInjectableTag;
          element.replaceWith(newScript);
        }
      });
    } // Simply replace with the decoded string, no special parsing or injection needed
    else {
      Logger.info(
        'option',
        `Replacing HTML for "${this.identifier}" without applying special code injection or position.`
      );
      const decodedContainer = document.createElement('div');
      decodedContainer.innerHTML = decodedString;
      encodedContainer.replaceWith(...decodedContainer.children);
    }
  }

  /**
   * Move an element to a specific location in the DOM.
   *
   * @param {HTMLElement} element Element to move
   * @param {string} position Position to move the element to
   * @returns {HTMLElement} The moved element
   */
  private moveElementToPosition(element: HTMLElement, position: string): HTMLElement {
    switch (position) {
      case InjectPosition.HeadStart:
        return document.head.insertAdjacentElement('afterbegin', element) as HTMLElement;
      case InjectPosition.HeadEnd:
        return document.head.insertAdjacentElement('beforeend', element) as HTMLElement;
      case InjectPosition.BodyStart:
        return document.body.insertAdjacentElement('afterbegin', element) as HTMLElement;
      case InjectPosition.BodyEnd:
        return document.body.insertAdjacentElement('beforeend', element) as HTMLElement;
      default:
        // Keep element where it was found in the first place
        return element;
    }
  }
}
